import { useCallback, useRef, useState } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { Loader2, Paperclip, Upload, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { api } from "@/lib/api-client"
import { cn } from "@/lib/utils"

import { NodeEditDialog } from "./node-edit-dialog"
import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { cleanupWorkflowAttachment } from "../../utils/workflow-attachment-utils"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"

interface UploadResponse {
  blobPath: string
  fileName: string
}

/**
 * Attachment node (#35): uploads a file into the workflow-attachments blob
 * container; its extracted text is injected into the flow's file context at run
 * time. Upload requires a saved workflow (we need its id for the blob path).
 */
export function AttachmentNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const workflowId = useWorkflowStore((state) => state.workflowId)
  const readOnly = useWorkflowStore((state) => state.readOnly)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fileName = (data.fileName as string) || ""
  const hasOutput = isCompleted && !!output

  const handleDelete = useCallback(() => {
    if (readOnly) return
    void deleteElements({ nodes: [{ id }] })
  }, [id, readOnly, deleteElements])

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      if (readOnly) return
      if (!workflowId) {
        setError("Save the workflow first, then upload.")
        return
      }
      const previousBlobPath = data.blobPath
      setUploading(true)
      setError(null)
      try {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("workflow_id", workflowId)
        formData.append("node_id", id)
        const res = (await api.post(
          "/explorer/workflow/attachment",
          formData
        )) as unknown as UploadResponse
        updateNodeData(id, {
          sourceType: "upload",
          blobPath: res.blobPath,
          fileName: res.fileName || file.name,
        })
        if (previousBlobPath && previousBlobPath !== res.blobPath) {
          try {
            await cleanupWorkflowAttachment(previousBlobPath)
          } catch {
            setError("Previous file cleanup failed. The new upload was saved.")
          }
        }
      } catch {
        setError("Upload failed. Please try again.")
      } finally {
        setUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ""
      }
    },
    [data.blobPath, id, readOnly, workflowId, updateNodeData]
  )
  const renderEditor = () => (
    <div className="grid gap-2">
      <input ref={fileInputRef} className="hidden" type="file" onChange={handleFileChange} />
      <Button
        className="nodrag w-full text-xs"
        disabled={readOnly || uploading || !workflowId}
        size="sm"
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? <Loader2 className="size-3 animate-spin" /> : <Upload className="size-3" />}
        {fileName ? "Replace file" : "Upload file"}
      </Button>
      {fileName && (
        <p className="text-muted-foreground truncate text-[11px]" title={fileName}>
          {fileName}
        </p>
      )}
      {!workflowId && (
        <p className="text-muted-foreground text-[10px]">Save the workflow to enable uploads.</p>
      )}
      {error && <p className="text-destructive text-[10px]">{error}</p>}
      <Label className="text-muted-foreground text-[10px] font-normal">
        The file's text is added to this flow's context.
      </Label>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[240px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[2])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <Paperclip className="size-4" />
            Attachment
            <NodeEditDialog description="Edit this attachment node." title="Attachment settings">
              {renderEditor()}
            </NodeEditDialog>
          </CardTitle>
          {selected && !readOnly && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
